import { useEffect, useRef, useState } from 'react';

interface IScrollFadeState {
  top: boolean;
  bottom: boolean;
}

export const useScrollFade = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  const [fade, setFade] = useState<IScrollFadeState>({ top: false, bottom: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      setFade({
        top: el.scrollTop > 0,
        bottom: el.scrollTop + el.clientHeight < el.scrollHeight - 1,
      });
    };

    update();
    el.addEventListener('scroll', update, { passive: true });

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    for (const child of Array.from(el.children)) {
      resizeObserver.observe(child);
    }

    const mutationObserver = new MutationObserver(() => {
      resizeObserver.disconnect();
      resizeObserver.observe(el);
      for (const child of Array.from(el.children)) {
        resizeObserver.observe(child);
      }
      update();
    });
    mutationObserver.observe(el, { childList: true, subtree: false });

    return () => {
      el.removeEventListener('scroll', update);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return { ref, fade };
};
